export enum IntervalResetUnit {
  Hours = 'HOURS',
  Days = 'DAYS',
  Weeks = 'WEEKS',
}

export enum AnchorPeriod {
  AM = 'AM',
  PM = 'PM',
}

export const INTERVAL_RESET_UNIT_CONFIG = {
  [IntervalResetUnit.Hours]: {
    label: 'Hour(s)',
    labelPlural: 'Hours',
    labelSingular: 'Hour',
    maxQuantity: 744,
    hoursPerUnit: 1,
  },
  [IntervalResetUnit.Days]: {
    label: 'Day(s)',
    labelPlural: 'Days',
    labelSingular: 'Day',
    maxQuantity: 31,
    hoursPerUnit: 24,
  },
  [IntervalResetUnit.Weeks]: {
    label: 'Week(s)',
    labelPlural: 'Weeks',
    labelSingular: 'Week',
    maxQuantity: 4,
    hoursPerUnit: 24 * 7,
  },
};

interface IntervalResetInput {
  quantity: number;
  unit: IntervalResetUnit;
}

type IntervalResetValidation =
  | { success: true; quantity: number; hours: number }
  | { success: false; error: string };

type ParsedAnchorTimestamp =
  | { success: true; anchorTimestamp: number }
  | { success: false; error: string };

/** Narrows Radix select values back to the interval units supported by policies. */
export function isIntervalResetUnit(value: string): value is IntervalResetUnit {
  return (
    value === IntervalResetUnit.Hours ||
    value === IntervalResetUnit.Days ||
    value === IntervalResetUnit.Weeks
  );
}

/**
 * Normalizes persisted hours into the most user-friendly unit for editing.
 *
 * Examples:
 * - `168` hours becomes `1 Week(s)`.
 * - `48` hours becomes `2 Day(s)`.
 * - `25` hours stays `25 Hour(s)` because it is not a whole day.
 */
export function hoursToIntervalResetInput(hours: number): IntervalResetInput {
  if (
    hours % INTERVAL_RESET_UNIT_CONFIG[IntervalResetUnit.Weeks].hoursPerUnit ===
    0
  ) {
    const quantity =
      hours / INTERVAL_RESET_UNIT_CONFIG[IntervalResetUnit.Weeks].hoursPerUnit;
    if (
      quantity >= 1 &&
      quantity <=
        INTERVAL_RESET_UNIT_CONFIG[IntervalResetUnit.Weeks].maxQuantity
    ) {
      return { quantity, unit: IntervalResetUnit.Weeks };
    }
  }

  if (
    hours % INTERVAL_RESET_UNIT_CONFIG[IntervalResetUnit.Days].hoursPerUnit ===
    0
  ) {
    const quantity =
      hours / INTERVAL_RESET_UNIT_CONFIG[IntervalResetUnit.Days].hoursPerUnit;
    if (
      quantity >= 1 &&
      quantity <= INTERVAL_RESET_UNIT_CONFIG[IntervalResetUnit.Days].maxQuantity
    ) {
      return { quantity, unit: IntervalResetUnit.Days };
    }
  }

  return {
    quantity: hours,
    unit: IntervalResetUnit.Hours,
  };
}

/** Formats persisted hours using the same friendly unit shown by the editor. */
export function formatIntervalReset(hours: number): string {
  const intervalReset = hoursToIntervalResetInput(hours);
  const config = INTERVAL_RESET_UNIT_CONFIG[intervalReset.unit];

  return `${intervalReset.quantity} ${
    intervalReset.quantity === 1 ? config.labelSingular : config.labelPlural
  }`;
}

/** Keeps the numeric interval field free of pasted letters, symbols, or spaces. */
export function sanitizeIntervalQuantityInput(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Validates the interval draft without clamping it, so users can see why `0` or
 * `999` is invalid before saving.
 */
export function validateIntervalResetInput(
  quantityValue: string,
  unit: IntervalResetUnit
): IntervalResetValidation {
  const config = INTERVAL_RESET_UNIT_CONFIG[unit];
  if (quantityValue === '') {
    return { success: false, error: 'Enter an interval quantity' };
  }

  const quantity = Number(quantityValue);
  if (!Number.isSafeInteger(quantity)) {
    return { success: false, error: 'Enter a whole number.' };
  }

  if (quantity < 1 || quantity > config.maxQuantity) {
    return {
      success: false,
      error: `Enter a value from 1 through ${config.maxQuantity} ${config.labelPlural.toLowerCase()}.`,
    };
  }

  return {
    success: true,
    quantity,
    hours: quantity * config.hoursPerUnit,
  };
}

/** Applies policy bounds when converting already-valid interval input to hours. */
function clampIntervalQuantity(
  quantity: number,
  unit: IntervalResetUnit
): number {
  const { maxQuantity } = INTERVAL_RESET_UNIT_CONFIG[unit];
  return Math.min(maxQuantity, Math.max(1, quantity));
}

/** Converts an editor interval unit and quantity into the backend's hours field. */
export function intervalResetInputToHours({
  quantity,
  unit,
}: IntervalResetInput): number {
  const config = INTERVAL_RESET_UNIT_CONFIG[unit];
  return clampIntervalQuantity(quantity, unit) * config.hoursPerUnit;
}

/** Formats a stored UTC timestamp as the user's local `MM/DD/YYYY` date field. */
export function formatAnchorDateInput(timestampSeconds: number): string {
  const date = new Date(timestampSeconds * 1000);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

/** Formats a stored UTC timestamp as the user's local 12-hour clock hour (1–12, no leading zero). */
export function formatAnchorHourInput(timestampSeconds: number): string {
  const date = new Date(timestampSeconds * 1000);
  const hour24 = date.getHours();
  const hour12 = hour24 % 12 || 12;
  return String(hour12);
}

/** Derives the AM/PM switch state from a stored UTC timestamp in local time. */
export function getAnchorPeriodInput(timestampSeconds: number): AnchorPeriod {
  const date = new Date(timestampSeconds * 1000);
  return date.getHours() >= 12 ? AnchorPeriod.PM : AnchorPeriod.AM;
}

/**
 * Parses typed local date/hour inputs and returns unix seconds.
 *
 * The date is intentionally interpreted with `new Date(year, month, day, hour)`
 * so `05/04/2026 + 3 + PM` means 3 PM in the user's local timezone, then stores
 * that moment as UTC epoch seconds for the backend.
 */
export function parseTypedAnchorToUnixHourStart(
  dateValue: string,
  hourValue: string,
  period: AnchorPeriod,
  now: Date = new Date()
): ParsedAnchorTimestamp {
  const trimmedDate = dateValue.trim();
  // Regex to match the date format MM/DD/YYYY
  const dateMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmedDate);
  if (!dateMatch) {
    return { success: false, error: 'Enter a date in MM/DD/YYYY format' };
  }

  const [, monthValue, dayValue, yearValue] = dateMatch;
  const month = Number(monthValue);
  const day = Number(dayValue);
  const year = Number(yearValue);
  const currentYear = now.getFullYear();
  // Only allow current year +- 1
  if (year < currentYear - 1 || year > currentYear + 1) {
    return {
      success: false,
      error: `Enter a year from ${currentYear - 1} through ${currentYear + 1}`,
    };
  }

  const trimmedHour = hourValue.trim();
  // Regex to match 1–12 only
  if (!/^([1-9]|1[0-2])$/.test(trimmedHour)) {
    return { success: false, error: 'Enter an hour from 1-12' };
  }

  const hour12 = Number(trimmedHour);
  const hour24 =
    period === AnchorPeriod.AM ? hour12 % 12 : hour12 === 12 ? 12 : hour12 + 12;

  const date = new Date(year, month - 1, day, hour24, 0, 0, 0);
  // Case: Invalid date, year, month, or day return error
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return { success: false, error: 'Enter a valid calendar date.' };
  }

  // Return timestamp as UTC epoch seconds
  return { success: true, anchorTimestamp: Math.floor(date.getTime() / 1000) };
}
